"use client";

import { useState, useCallback } from "react";

interface AIAction {
  type: string;
  data: Record<string, unknown>;
  message?: string;
}

interface AIResponse {
  message: string;
  actions: AIAction[];
  suggestions?: string[];
}

interface UseAIAgentOptions {
  onAction?: (action: AIAction) => void;
}

export function useAIAgent(options: UseAIAgentOptions = {}) {
  const [loading, setLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState<AIResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(async (
    message: string,
    context: {
      currentOrder?: unknown;
      orderType?: string;
      tableNumber?: string | null;
    } = {}
  ): Promise<AIResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/ai/agent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          context,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "AI agent request failed");
      }

      const data: AIResponse = await res.json();
      setLastResponse(data);

      // Execute actions
      if (data.actions && options.onAction) {
        for (const action of data.actions) {
          options.onAction(action);
        }
      }

      return data;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error";
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [options]);

  return {
    sendMessage,
    loading,
    lastResponse,
    error,
    suggestions: lastResponse?.suggestions || [],
  };
}
